import { ROOT_FOLDER } from '@/consts';
import { BASEURL, Endpoint, ENDPOINTS, SPXAPI } from '../../../api';
import { type FilePermissionActions, FileStatus, SecuredFile, UserIdentity } from '../../../types/types';
import {
    FilesListType,
    FilesQueryParams,
    LoadParams,
    Ordering,
} from '../interfaces';
import { Dict, dictToQuery, eqUserIdentities } from '../../../components/utils';
import { parseFolderId } from './folders';
import getFilePermissions from '../../../api/requests/getFilePermissions.ts';

const EXCLUDE_STATUSES: FileStatus[] = ['error', 'exporting', 'uploading', 'scanning', 'cleaning', 'scan_blocked', 'deleted', 'missing'];
const EXCLUDE_STATUSES_STR: string = EXCLUDE_STATUSES.join(',');

/**
 * Query fragment that filters out deleted/transitional files from a folder
 * listing. Reuse this anywhere folder children are fetched so freshly deleted
 * files (which linger in the `deleted` status until the backend purges them)
 * are not shown. See SPX: "Deleted files displayed in Upload Drawer".
 */
export const EXCLUDE_STATUSES_QUERY = `exclude_statuses=${EXCLUDE_STATUSES_STR}`;
const CO_OWNER_ACTIONS: FilePermissionActions[] = ['manage_access', 'view_logs'];

/* eslint-disable camelcase */
interface QueryDict extends Omit<FilesQueryParams, 'filesListType' | 'ordering'> {
    readonly order_by?: string;
}

const orderDirectionToQuery = (isDescending: boolean): string => (
    isDescending ? '-' : ''
);

const orderingToQuery = (ordering: Ordering): string => (
    ordering
        ? `${orderDirectionToQuery(ordering.isDescending)}${ordering.orderBy}`
        : ''
);

export const getFilesQuerystring = ({
    filesListType,
    email,
    parent_folder,
    search_string,
    ordering,
    ...rest
}: FilesQueryParams): string => {
    /*
    * Because search is recursive, it's incompatible with folder navigation.
    * In current UX folder should have more priority than search
    * */
    const parenFolderQueryParam = parent_folder === ROOT_FOLDER ? '' : parent_folder;
    const queryDict: QueryDict = {
        ...rest,
        parent_folder: parenFolderQueryParam,
        search_string: parenFolderQueryParam ? '' : search_string,
        email: filesListType === 'allFiles' && !parenFolderQueryParam ? email : null,
        order_by: orderingToQuery(ordering),
    };
    let query = `?${dictToQuery(queryDict as unknown as Dict)}`;
    if (filesListType === 'sharedFiles') {
        query = `${query}&sharedWithMe=True`;
    } else if (filesListType === 'allFiles') {
        query = `/all${query}`;
    }
    return `${query}&exclude_statuses=${EXCLUDE_STATUSES_STR}`;
};
/* eslint-enable camelcase */

export const loadParamsToQueryParams = ({
    folderKey,
    ...rest
}: LoadParams): FilesQueryParams => ({
    ...rest,
    parent_folder: parseFolderId(folderKey),
});

interface DeleteFilePayload {
    readonly file: SecuredFile;
    readonly filesListType: FilesListType;
    readonly currentUserIdentity: UserIdentity;
}

export const getDeleteFileEndpoint = async ({
    file,
    filesListType,
    currentUserIdentity,
}: DeleteFilePayload, API: SPXAPI): Promise<Endpoint> => {
    const { file_id: fileId } = file;
    const isOwner = eqUserIdentities(currentUserIdentity, { id: file.owner_id, email: file.owner_email });

    // SPX-3773: manage_access can be inherited from a workspace ancestor. In
    // that case the user has no direct permission row on the file but should
    // still be able to delete it. The backend already reflects the effective
    // right via `menu.edit_shares` (true for owner or any user with
    // manage_access, direct or inherited). Trust that signal to route to
    // deleteFile; the BE re-enforces the auth check on the actual call.
    if (filesListType !== 'sharedFiles' || isOwner || file.menu?.edit_shares) {
        return ENDPOINTS.deleteFile(fileId);
    }

    // Fallback for files whose `menu.edit_shares` isn't present (e.g. cached
    // SecuredFile from before the BE started emitting it): preserve the old
    // direct-permission-row + group-membership co-owner detection so this
    // change is safe across BE/FE rollout windows.
    const { users } = await getFilePermissions(API, file.file_id);
    const { groups } = await API.get(BASEURL.backend(), ENDPOINTS.getMyUserInfo('groups'), {});
    const isDirectCoOwner = users.some((x) => (
        (x.userId === currentUserIdentity.id
            || (x.recipientType !== 'group' && x.email.toLowerCase() === currentUserIdentity.email.toLowerCase())
            || (x.recipientType === 'group' && groups.includes(x.userId)))
        && CO_OWNER_ACTIONS.every((action) => x.actions.includes(action))
    ));

    return isDirectCoOwner
        ? ENDPOINTS.deleteFile(fileId)
        : ENDPOINTS.shareFile(fileId, currentUserIdentity.id);
};
